import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const useIsAdmin = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const lastCheckedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id ?? null;

    // Only reset state when user actually changes (login/logout),
    // not on token refreshes which keep the same user id.
    if (userId !== lastCheckedUserIdRef.current) {
      setLoading(true);
      setError(null);
      if (!userId) setIsAdmin(false);
    }

    const checkAdminRole = async () => {
      if (!user) {
        lastCheckedUserIdRef.current = null;
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc("has_role", {
          _user_id: user.id,
          _role: "admin",
        });

        if (error) {
          console.error("Error checking admin role:", error);
          setError(error);
          setIsAdmin(false);
        } else {
          setError(null);
          setIsAdmin(data === true);
        }
      } catch (err) {
        console.error("Error checking admin role:", err);
        setError(err);
        setIsAdmin(false);
      } finally {
        lastCheckedUserIdRef.current = user.id;
        setLoading(false);
      }
    };

    checkAdminRole();
  }, [user]);

  return { isAdmin, loading, error };
};

